import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import moment from 'moment'
import 'moment/locale/ja'
import { Panel } from '../../../atoms'
import TourDrawer from 'components/organisms/Tours/Weekly/Drawer/TourDrawer'
import { agentService, utilService } from '../../../../core/services'
import * as interfaces from '../../../../static/interfaces'
import * as constants from 'static/constants'
import ExpandableLayout from '../../_layouts/expandableLayout'
import { buildTours, filteredTours } from 'utils/weekly'
import { IWeeklyPost } from 'utils/weeklyTypes'
import WeeklySettings from 'components/organisms/Tours/Weekly/WeeklySettings'
import Provider from 'core/contexts/weekly/Provider'
import { useWeeklyContext } from 'core/contexts/weekly/WeeklyContext'
import { TourDrawerProvider } from 'core/contexts/tour/TourDrawerContext'
import { WeeklyTable } from 'components/organisms/Tours/Weekly/WeeklyTable'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: interfaces.IWindow
moment.updateLocale('ja', {
  meridiem(hour, minute, isLowercase) {
    if (hour >= 12) {
      return isLowercase ? 'pm' : 'PM'
    } else {
      return isLowercase ? 'am' : 'AM'
    }
  },
})

interface IProps {
  currentUser: interfaces.IUser
  tours: any
  no_booking_tours: any
  calendar_memos: any
  posts: any
  start_date: any
  user: any
  tasks: any[]
  tour_memo_templates?: any[]
}

const urlParams = new URLSearchParams(window.location.search)
const initialFilterOptions = {
  post_ids: urlParams.getAll('post_ids[]')?.map(id => Number(id)) || [],
  post_types: urlParams
    .getAll('post_types[]')
    .filter((t): t is IWeeklyPost['post_type'] => ['private', 'group'].includes(t)),
  tour_group_status: urlParams.getAll('tour_group_status[]') || [],
  alert: ['true', 'false'].includes(urlParams.get('alert'))
    ? urlParams.get('alert') === 'true'
    : undefined,
  insufficient_with_guide: ['true', 'false'].includes(urlParams.get('insufficient_with_guide'))
    ? urlParams.get('insufficient_with_guide') === 'true'
    : undefined,
  insufficient_with_no_guide: ['true', 'false'].includes(
    urlParams.get('insufficient_with_no_guide')
  )
    ? urlParams.get('insufficient_with_no_guide') === 'true'
    : undefined,
  connected: ['true', 'false'].includes(urlParams.get('connected'))
    ? urlParams.get('connected') === 'true'
    : undefined,
  not_connected: ['true', 'false'].includes(urlParams.get('not_connected'))
    ? urlParams.get('not_connected') === 'true'
    : undefined,
  all_task_done: ['true', 'false'].includes(urlParams.get('all_task_done'))
    ? urlParams.get('all_task_done') === 'true'
    : undefined,
  has_task: ['true', 'false'].includes(urlParams.get('has_task'))
    ? urlParams.get('has_task') === 'true'
    : undefined,
}
// Contextを使用する内部コンポーネント
const WeeklyContent: React.FC<IProps> = props => {
  const {
    filterOptions,
    setFilterOptions,
    selectedTour,
    setSelectedTour,
    startDate,
    allTours,
    setAllTours,
    onTourUpdate,
  } = useWeeklyContext()

  const { data: user } = utilService.getDataFromJson(props.user)
  const { data: initialTours } = utilService.getDataFromJson(props.tours)
  const { data: noBookingTours } = utilService.getDataFromJson(props.no_booking_tours)
  const { data: posts } = utilService.getDataFromJson(props.posts)
  const { data: tasks } = utilService.getDataFromJson(props.tasks)
  const { data: tourMemoTemplates } = utilService.getDataFromJson(props.tour_memo_templates)

  // UI用state（Contextに移動していないもの）
  const [hideAllTourGroup, setHideAllTourGroup] = React.useState(true)
  const [groupSelectingResource, setGroupSelectingResource] = React.useState(null)
  const [groupSelectedId, setGroupSelectedId] = React.useState(null)

  // useMemo
  const currentTours = React.useMemo(() => {
    return filteredTours(allTours, filterOptions)
  }, [allTours, filterOptions])

  // useEffect
  React.useEffect(() => {
    const newTours = buildTours(
      initialTours.concat(noBookingTours),
      posts,
      tasks,
      tourMemoTemplates
    )
    setAllTours(newTours)

    // URL共有用に開始日をURLにセット
    urlParams.set('start', startDate.format('YYYY-MM-DD'))
    window.history.replaceState(null, '', `/agent/tours/weekly?${urlParams.toString()}`)

    // Scrollbar visible or not js
    const scrollArea = document.getElementById('tbody')
    const tHead = document.getElementById('thead')
    const table = document.getElementById('table')

    // Get the scrollbar width
    const scrollbarWidth = scrollArea.offsetWidth - scrollArea.clientWidth

    if (scrollbarWidth > 0) {
      tHead.style.width = `calc(100% - ${scrollbarWidth}px)`
    } else {
      table.style.borderRight = '0'
    }
  }, [])

  // URLパラメータから初期表示ツアーをセット
  const hasRun = React.useRef(false)
  React.useEffect(() => {
    if (currentTours.length > 0) {
      // 初回のみ実行
      if (!hasRun.current) {
        hasRun.current = true
        const currentUrlParams = new URLSearchParams(window.location.search)
        const initialTourId = currentUrlParams.get('tour_id')
        if (currentTours.length > 0 && initialTourId) {
          const newSelectedTour = currentTours.find(tour => tour.id === Number(initialTourId))
          setSelectedTour(newSelectedTour)
          document.body.style.overflow = 'hidden'
        }
      }
    }
  }, [currentTours])

  React.useEffect(() => {
    if (!groupSelectingResource) {
      setGroupSelectedId(null)
    }
  }, [groupSelectingResource])

  const handleClickTour = React.useCallback(
    tour => {
      const currentUrlParams = new URLSearchParams(window.location.search)
      setSelectedTour(tour)
      currentUrlParams.set('tour_id', tour.id)
      window.history.replaceState(null, '', `/agent/tours/weekly?${currentUrlParams.toString()}`)
      document.body.style.overflow = 'hidden'
    },
    [urlParams]
  )

  const handleCloseTourDrawer = React.useCallback(() => {
    const currentUrlParams = new URLSearchParams(window.location.search)
    setSelectedTour(null)
    currentUrlParams.delete('tour_id')
    window.history.replaceState(null, '', `/agent/tours/weekly?${currentUrlParams.toString()}`)
    document.body.style.overflow = 'unset'
  }, [])

  // ツアーの情報を直接変更するものではないが、結果的にツアーの変更が行われる時に使用する
  const refetchWeeklyTour = async (tour_id: number) => {
    try {
      const { tour: refetchedTour } = await agentService.getWeeklyTour(tour_id)
      onTourUpdate(refetchedTour)
    } catch {
      location.reload()
    }
  }

  return (
    <ExpandableLayout user={user} disableMaxWidth={true}>
      <Wrapper>
        <Panel marginType="dashboard" className="weekly">
          <WeeklySettings
            posts={posts}
            startDate={startDate}
            filterOptions={filterOptions}
            setFilterOptions={setFilterOptions}
          />
          <WeeklyTable
            startDate={startDate}
            initialTours={initialTours}
            noBookingTours={noBookingTours}
            currentTours={currentTours}
            hideAllTourGroup={hideAllTourGroup}
            handleClickTour={handleClickTour}
          />
          <div
            onClick={() => setHideAllTourGroup(!hideAllTourGroup)}
            className="toggleDisplayBtn"
            role="button"
          >
            <span>
              {hideAllTourGroup ? (
                <SvgIcon
                  name="expand_more"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
              ) : (
                <SvgIcon
                  name="expand_less"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
              )}
            </span>
            {I18n.t(`generic.${hideAllTourGroup ? 'show_all' : 'hide_all'}`, { locale: 'ja' })}
          </div>
        </Panel>
        <TourDrawerProvider
          groupSelectingResource={groupSelectingResource}
          setGroupSelectingResource={setGroupSelectingResource}
          groupSelectedId={groupSelectedId}
          setGroupSelectedId={setGroupSelectedId}
        >
          {selectedTour && (
            <TourDrawer
              currentUser={props.currentUser}
              tour={selectedTour}
              show={!!selectedTour}
              refetchWeeklyTour={refetchWeeklyTour}
              handleClose={handleCloseTourDrawer}
            />
          )}
        </TourDrawerProvider>
      </Wrapper>
    </ExpandableLayout>
  )
}

// メインコンポーネント（Providerでラップ）
const Weekly: React.FC<IProps> = props => {
  const start_date = moment(props.start_date)
  const { data: calendar_memos } = utilService.getDataFromJson(props.calendar_memos)
  const { data: posts } = utilService.getDataFromJson(props.posts)
  const { data: tasks } = utilService.getDataFromJson(props.tasks)
  const { data: tourMemoTemplates } = utilService.getDataFromJson(props.tour_memo_templates)

  return (
    <Provider
      initialFilterOptions={initialFilterOptions}
      initialStartDate={start_date}
      initialCalendarMemos={calendar_memos}
      posts={posts}
      tasks={tasks}
      tourMemoTemplates={tourMemoTemplates}
    >
      <WeeklyContent {...props} />
    </Provider>
  )
}

const Wrapper = styled.div`
  .Panel_Body {
    max-width: none;
    min-width: 840px;
  }

  .toggleDisplayBtn {
    position: absolute;
    bottom: 56px;
    right: 56px;
    display: flex;
    align-items: center;
    padding: 8px 20px 8px 12px;
    font-size: 14px;
    border: 1px solid ${constants.COLORS.divider};
    background-color: ${constants.COLORS.white};
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    transition: color 0.2s;
    cursor: pointer;
    &:hover {
      color: ${constants.COLORS.primary};
    }
    span {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      margin-right: 8px;
      background-color: ${constants.COLORS.primary};
      border-radius: 50%;
      .icon {
        width: 14px;
        height: 14px;
        color: ${constants.COLORS.white};
      }
    }
  }
`

export default Weekly
